import {
  defineFilter,
  defineSearchFilters,
  type SearchFieldModelValue
} from '../components/search/stakeholder-search-operators'
import {
  buildFilterQuery,
  selectActiveFilters,
  type StakeholderFilterField,
  type StakeholderFilterSnapshot
} from '../model/search-decisions'
import type { StakeholderType, GatshipSearchOperator } from '@gat-ship/types'
import {
  IconFlag,
  IconGlobe,
  IconHash,
  IconMail,
  IconPhone,
  IconUser,
  type IconComponent
} from '@gat-ship/icons'

export const stakeholderSearchFilterActiveStates = {
  active: 'active',
  inactive: 'inactive',
  all: 'all'
} as const

export type StakeholderSearchFilterActiveState =
  (typeof stakeholderSearchFilterActiveStates)[keyof typeof stakeholderSearchFilterActiveStates]

/**
 * Snapshot the reactive filter set into the plain-data shape the pure core
 * consumes — reads each ref's value and preserves key order so the resulting
 * query string is byte-identical.
 */
export function snapshotStakeholderFilters(
  filters: ReturnType<typeof useStakeholderSearchFilters>
): StakeholderFilterSnapshot {
  const toField = (
    filter: ReturnType<typeof defineFilter>
  ): StakeholderFilterField => ({
    fieldName: filter.fieldName,
    operator: filter.operator.value,
    filterValue: filter.filterValue.value
  })
  return {
    type: toField(filters.type),
    name: toField(filters.name),
    email: toField(filters.email),
    phone: toField(filters.phone),
    state: toField(filters.state),
    country: toField(filters.country),
    vendorNumber: toField(filters.vendorNumber),
    clientNumber: toField(filters.clientNumber)
  }
}

export function buildStakeholderFilterQuery(
  filters: ReturnType<typeof useStakeholderSearchFilters>
): string {
  return buildFilterQuery(snapshotStakeholderFilters(filters))
}

export interface CurrentSearch {
  searchTerm?: string
  activeFilters: Record<
    string,
    {
      filterValue: string | undefined
      operator: GatshipSearchOperator
      fieldName: SearchFieldModelValue['fieldName']
    }
  >
}

export function getCurrentSearchFromFilters(
  filters: ReturnType<typeof useStakeholderSearchFilters>
): CurrentSearch {
  return {
    activeFilters: selectActiveFilters(snapshotStakeholderFilters(filters))
  }
}

export function useStakeholderSearchFilters() {
  return defineSearchFilters({
    type: defineFilter({
      fieldName: 'type',
      defaultOperator: 'equals',
      defaultFilterValue: 'client' satisfies StakeholderType,
      allowedOperators: {
        equals: true
      }
    }),
    name: defineFilter({
      fieldName: 'name',
      defaultOperator: 'contains',
      allowedOperators: {
        contains: true,
        equals: true,
        like: true
      }
    }),
    email: defineFilter({
      fieldName: 'email',
      defaultOperator: 'contains',
      allowedOperators: {
        contains: true,
        equals: true
      }
    }),
    phone: defineFilter({
      fieldName: 'phone',
      defaultOperator: 'equals',
      allowedOperators: {
        equals: true
      }
    }),
    state: defineFilter({
      fieldName: 'state',
      defaultOperator: 'equals',
      defaultFilterValue: 'active' satisfies StakeholderSearchFilterActiveState,
      allowedOperators: {
        equals: true
      }
    }),
    country: defineFilter({
      fieldName: 'country',
      defaultOperator: 'equals',
      allowedOperators: {
        equals: true
      }
    }),

    /** Only for vendor type */
    vendorNumber: defineFilter({
      fieldName: 'vendorNumber',
      defaultOperator: 'equals',
      allowedOperators: {
        equals: true
      }
    }),

    /** Only for client type */
    clientNumber: defineFilter({
      fieldName: 'clientNumber',
      defaultOperator: 'equals',
      allowedOperators: {
        equals: true
      }
    })
  })
}

export type StakeholderSearchFilterKeys = keyof ReturnType<
  typeof useStakeholderSearchFilters
>

/** A mapping of filter field names to their metadata. */
export const filterFieldMetadata: Record<
  StakeholderSearchFilterKeys,
  {
    title: string
    icon: IconComponent
  }
> = {
  type: {
    title: 'Type',
    icon: IconUser
  },
  name: {
    title: 'Name',
    icon: IconUser
  },
  email: {
    title: 'Email',
    icon: IconMail
  },
  phone: {
    title: 'Phone',
    icon: IconPhone
  },
  state: {
    title: 'Status',
    icon: IconFlag
  },
  country: {
    title: 'Country',
    icon: IconGlobe
  },
  vendorNumber: {
    title: 'Vendor Number',
    icon: IconHash
  },
  clientNumber: {
    title: 'Client Number',
    icon: IconHash
  }
}
